import { Component, OnDestroy, OnInit, AfterViewInit, Output, EventEmitter } from '@angular/core';
import { OperationService } from '../../../api';
import { ProfileService } from '../../../core/profile/profile.service';
import { FinishProService } from '../../services/finish-pro.service';
import { Subscription } from 'rxjs';
import { LoadingProService } from '../../services/loading-pro.service';
import { PersonalizationsService } from '../../../base/services/personalizations/personalizations.service';
import { Environment } from '../../../models/environment';
import { MessageService } from '../../../shared/message/message.service';
import { TranslateService } from '../../../core/translation';
import { MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { cValidators } from '../../../models/validator';

const OTP_INPUT_LENGTH = 8;
const REMAINING_TIME = 180;

@Component({
  selector: 'app-access-code',
  templateUrl: './access-code.component.html',
  styleUrls: ['./access-code.component.scss'],
})
export class AccessCodeComponent implements OnInit, OnDestroy, AfterViewInit {

  form: FormGroup = new FormGroup({
    nif: new FormControl('', [Validators.required]),
    otp: new FormControl('', [Validators.required, Validators.minLength(OTP_INPUT_LENGTH), Validators.maxLength(OTP_INPUT_LENGTH)])
  })

  @Output() addOTP = new EventEmitter<any>(true);

  @Output() errorOTP = new EventEmitter<any>(true);

  otpCode: string;

  errorCode: number = 3;

  isCodeExpired: boolean = false;

  private subscription: Subscription;

  signatureBlock: boolean = false;

  token: string;

  userCreatorEmail: string;

  mobilePhone: string;

  forwardingOTPButton: boolean = false;

  environment: Environment;

  configInput = {
    allowNumbersOnly: true,
    length: OTP_INPUT_LENGTH,
    inputStyles: {
      color: '#52707A',
      width: '32px',
      height: '40px',
      border: '1px solid #92ADCC',
      background: '#F7FAFD',
      'font-size': '16px',
    },
  };

  operationType: string;

  timmer: string = null;

  isLoading: boolean = false;

  otpError: boolean = false;

  intervalId: NodeJS.Timeout;

  isNIFReadonly: boolean;

  isNIFOptional: boolean = false;

  constructor(
    public _dialogRef: MatDialogRef<AccessCodeComponent>,
    private _profile: ProfileService,
    private _operation: OperationService,
    private _finishProService: FinishProService,
    private _loadingPro: LoadingProService,
    private _personalization: PersonalizationsService,
    private _message: MessageService,
    private _translate: TranslateService
  ) {
    this.environment = this._personalization.sendEnv;
    this.isNIFOptional = this.environment.features.otpDniOptional;
    this.token = this._profile.getPublicToken();
  }

  ngOnInit(): void {
    this.operationType = this._finishProService.operationDetailResponse.operationType;
    const disableValidatorsNIF = this.environment?.features?.disableValidators?.nif || false;
    if (!disableValidatorsNIF) {
      this.form.get('nif').setValidators([Validators.required, cValidators.nif]);
      this.form.get('nif')?.updateValueAndValidity();
    }

    if (this.environment.features.otpDniOptional) {
      const nif = this.form.get('nif');
      nif.clearValidators();
      nif.setErrors(null);
      nif.updateValueAndValidity();
    }

    this.setReceiverData();
    this.sendOTP();
  }

  ngAfterViewInit(): void {
    this._loadingPro.setLoader(false);
    this._loadingPro.setNextOperationLoader(false);
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  setReceiverData(): void {
    this.userCreatorEmail = this._finishProService.operationDetailResponse.userCreatorEmail;
    const nif = this._finishProService.operationDetailResponse?.nifUser || '';
    this.isNIFReadonly = !!nif;
    this.form.patchValue({ nif });
    if (nif) {
      this.form.get('nif').clearValidators();
      this.form.get('nif')?.updateValueAndValidity();
    }

    const mobile = this._finishProService.operationDetailResponse.metadata.filter((res) => res.key == 'CONTACT_PHONE');
    this.mobilePhone = mobile[0]?.value.slice(-3);
  }

  sendOTP(callback?: Function): void {
    this._operation.operationGetSendOTP(this._finishProService.operationId, this.environment.appid, this.token).then(
      () => {
        if (callback) callback(true);
        this.counter();
      },
      (error) => {
        this._message.showError(error.error.error.message);
        this.exitProcess();
        console.log(error);
        if (callback) callback(false);
      }
    );
  }

  forwardingOTP(): void {
    this.forwardingOTPButton = true;
    this.otpError = false;
    this.isCodeExpired = false;

    this.subscription = this._operation
      .operationGetForwardingOTP(this._finishProService.operationId, this.environment.appid, this.token)
      .subscribe(
        () => {
          this.timmer = null;
          this.stopCounter();
          this.counter();
        },
        (error) => {
          this.timmer = null;
          this.subscription.unsubscribe();
          this._message.showError(error);
        }
      );
    setTimeout(() => {
      this.forwardingOTPButton = false;
    }, 60000);
  }

  counter(): void {
    let remainingTime = REMAINING_TIME;

    this.intervalId = setInterval(() => {
      const minutes = String(Math.floor(remainingTime / 60)).padStart(2, '0');
      const seconds = String(remainingTime % 60).padStart(2, '0');
      this.timmer = `${minutes}:${seconds}`;
      if (remainingTime <= 0) {
        clearInterval(this.intervalId);
        this.isCodeExpired = true;
      } else {
        remainingTime--;
      }
    }, 1000);
  }

  stopCounter(): void {
    if (this.intervalId !== undefined) {
      clearInterval(this.intervalId);
    }
  }

  signOTP(): void {
    this.isLoading = true;
    const otpCode: string = this.form.get('otp').value;
    const nif: string = this.form.get('nif').value;
    try {
      this._finishProService.setOTPCode(otpCode);
      this._finishProService.setReceiverNIF(nif);
      this.addOTP.emit({ validated: true });
      this._finishProService.errorEvent.subscribe((res) => {
        this.isLoading = false;
        this.errorOTP.emit('error');
        if (res == 'IVC1152') {
          this._dialogRef.close({ validated: false });
          this.isLoading = false;
        }
      })
    } catch (error) {
      this.isLoading = false;
      console.log(error);
    }
  }

  setFormValue(form: { key: string, value: string }): void {
    this.form.patchValue({ [form.key]: form.value });
  }

  exitProcess(): void {
    this._dialogRef.close({ validated: false });
  }
}
